import React, { useState, useEffect, useCallback } from "react";
import { X, Plus, Loader2 } from "lucide-react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import useAnalysis from "../../services/useAnalysis";
import { AppUtils } from "../../services/AppUtils";
import "./MyCoreTeam.css";

const SwalAlert = withReactContent(Swal);

export const SearchModal = ({ isOpen, onClose, onAdd, existingMembers = [], summary = {} }) => {
  const { searchDistributor } = useAnalysis();
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const existingMembersRef = React.useRef(existingMembers);
  const [maxRequestsAllowed, setMaxRequestsAllowed] = useState(0);
  const [currentRequestsSent, setCurrentRequestsSent] = useState(0);

  const distributor = JSON.parse(localStorage.getItem("distributor") || "{}");
  const ranks = JSON.parse(localStorage.getItem("ranks") || "[]");

  // Calculate max requests allowed based on summary data
  const calculateMaxRequestsAllowed = useCallback(() => {
    const { maxMembers = 10, totalMembers = 0, pendingRequests = 0 } = summary;
    const maxAllowed = Math.max(0, maxMembers - totalMembers - pendingRequests);
    setMaxRequestsAllowed(maxAllowed);
    return maxAllowed;
  }, [summary]);

  // Update ref when modal opens and reset search state when modal closes
  useEffect(() => {
    if (isOpen) {
      existingMembersRef.current = existingMembers;
      calculateMaxRequestsAllowed();
      setCurrentRequestsSent(0);
    } else {
      // Reset search state when modal closes
      setSearchTerm("");
      setDebouncedSearchTerm("");
      setResults([]);
      setSearching(false);
      setCurrentRequestsSent(0);
    }
  }, [isOpen, existingMembers, calculateMaxRequestsAllowed]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search when debounced term changes
  useEffect(() => {
    if (!debouncedSearchTerm.trim()) {
      setResults([]);
      return;
    }

    setSearching(true);

    searchDistributor(
      {
        distributor_id: distributor.distributor_id,
        search_data: debouncedSearchTerm.trim(),
      },
      (response) => {
        console.log("Search results:", response);
        if (response?.data) {
          const transformedResults = response.data.map((item) => {
            const rank = ranks.find((r) => r.id === item.rank_id);
            const isAlreadyInTeam = existingMembersRef.current.some((member) => member.id === item.id.toString());
            const isOwnProfile = item.id.toString() === distributor.distributor_id;
            const hasReachedMaxRequests = currentRequestsSent >= maxRequestsAllowed;
            
            return {
              id: item.id.toString(),
              name: item.name,
              rank: rank?.title || "Unknown Rank",
              teamSize: item.teamSize || 0,
              joinDate: item.joinDate,
              rankId: item.rank_id,
              canAdd: !isAlreadyInTeam && !isOwnProfile && !hasReachedMaxRequests,
              reason: isAlreadyInTeam ? "Already in team" : 
                      isOwnProfile ? "You cannot add yourself" : 
                      hasReachedMaxRequests ? "Max requests reached" : null,
            };
          });
          setResults(transformedResults);
        } else {
          setResults([]);
        }
        setSearching(false);
      },
      (error) => {
        console.error("Search error:", error);
        setResults([]);
        setSearching(false);
        // Show user-friendly error message
        SwalAlert.fire({
          title: "Search Error",
          text: "Failed to search for distributors. Please try again.",
          icon: "error",
          button: "OK",
        });
      }
    );
  }, [debouncedSearchTerm]);

  if (!isOpen) return null;

  return (
    <div className="my-core-team-modal" onClick={onClose}>
      <div className="my-core-team-modal-content my-core-team-search-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="my-core-team-search-modal-header">
          <h2 className="my-core-team-search-modal-title">Add Team Member</h2>
          <button
            className="my-core-team-search-modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        <div className="my-core-team-search-input-container">
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                onClose();
              }
            }}
            className="my-core-team-search-input"
            autoFocus
          />
          {searching && (
            <div className="my-core-team-search-loading">
              <Loader2 size={16} className="animate-spin" />
              <span>Searching...</span>
            </div>
          )}
        </div>

        <div className="my-core-team-search-results">
          {/* Request Limit Info */}
          <div className="my-core-team-search-limit-info">
            <span>
              Requests: {currentRequestsSent}/{maxRequestsAllowed} 
              {maxRequestsAllowed === 0 && " (Team is full)"}
            </span>
          </div>
          
          {results.map((result) => (
            <div key={result.id} className="my-core-team-search-result-item">
              <div className="my-core-team-search-result-info">
                <h4 className="my-core-team-search-result-name">{result.name}</h4>
                <p className="my-core-team-search-result-details">
                  {result.rank} | Team Size: {AppUtils.formatNumber(result.teamSize)} | ID: {result.id} | Joined:{" "}
                  {AppUtils.formatJoinDate(result.joinDate)}
                </p>
              </div>
              <button
                className={`my-core-team-search-result-btn ${
                  result.canAdd ? "my-core-team-success-button" : "my-core-team-secondary-button"
                }`}
                onClick={() => {
                  if (result.canAdd) {
                    onAdd(result.id);
                    setCurrentRequestsSent(prev => prev + 1);
                  }
                }}
                disabled={!result.canAdd}
              >
                {result.canAdd ? (
                  <>
                    <Plus size={16} color="white" />
                    <span className="my-core-team-search-btn-text">Add</span>
                  </>
                ) : (
                  <>
                    <X size={16} />
                    <span className="my-core-team-search-btn-text">
                      {result.reason || (result.id === distributor.distributor_id ? "You" : "Added")}
                    </span>
                  </>
                )}
              </button>
            </div>
          ))}
          {results.length === 0 && !searching && searchTerm && (
            <div className="my-core-team-search-empty">
              No results found for "{searchTerm}"
            </div>
          )}
          {!searchTerm && (
            <div className="my-core-team-search-empty">
              Start typing to search for distributors...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
